import type {
  ParameterDimensionDefinitionInOut,
  ParameterValueCreateOperation,
} from '@festina/configuration-general-types/configuration'
import { dateUtils, notEmpty, type Optional } from '@festina-life/core'

import { stringToDateTimeNullSafe } from '../../uiModels/helpers'
import { partitionValueOperations } from '../../uiModels/parameterOperations/partitionValueOperations'
import type { ParameterValueEntityOperation } from '../../uiModels/parameterOperations/types'
import type { OperationWithSourceInfo } from '../../uiModels/types'
import type { ParameterValueRowData } from './types'

const EMPTY_VALUE = '[ANY]'

const flattenParameterValueCreateOperations = (
  operation: OperationWithSourceInfo<ParameterValueCreateOperation>,
  dimensionDefinitions: ParameterDimensionDefinitionInOut[],
) => {
  const { dimensionCount, dimensionsAndValues } = operation.internalOperation
    ?.entity ?? { dimensionCount: 0, dimensionsAndValues: [] as string[] }

  return (dimensionsAndValues ?? [])
    .filter(notEmpty)
    .map((dimensionsAndValueEntry) => {
      const rowData = dimensionsAndValueEntry.split(';')

      return {
        interval: {
          from: stringToDateTimeNullSafe(
            operation.internalOperation.entity?.fromDate,
            Number.NEGATIVE_INFINITY,
          ),
          to: stringToDateTimeNullSafe(
            operation.internalOperation.entity?.toDate,
            Number.POSITIVE_INFINITY,
          ),
        },
        dimensions:
          rowData
            .slice(0, dimensionCount ?? 0)
            .map((value, dimensionIndex) => ({
              value,
              definition: dimensionDefinitions.at(dimensionIndex),
            })) ?? [],
        value: rowData[rowData.length - 1],
        isGrouped: false,
      } satisfies ParameterValueRowData
    })
}

const isEmptyRangeDimension = (value: Optional<string>, isRange: boolean) =>
  (!value || value === EMPTY_VALUE) && isRange

const sortDimensionsByIsRange = (
  dimensions: ParameterValueRowData['dimensions'],
) =>
  dimensions.toSorted((dimension) => (dimension.definition?.isRange ? -1 : 1))

const sortParameterValueRowData = (
  a: ParameterValueRowData,
  b: ParameterValueRowData,
) => {
  if (a.interval.from !== b.interval.from) {
    return b.interval.from - a.interval.from
  } else {
    const aDimensions = sortDimensionsByIsRange(a.dimensions)
    const bDimensions = sortDimensionsByIsRange(b.dimensions)

    for (let i = 0; i < aDimensions.length; i++) {
      const aValue = aDimensions.at(i)?.value
      const aDefinition = aDimensions.at(i)?.definition
      const bValue = bDimensions.at(i)?.value
      const bDefinition = bDimensions.at(i)?.definition

      if (aValue === bValue) {
        continue
      }

      if (
        isEmptyRangeDimension(aValue, !!aDefinition?.isRange) ||
        isEmptyRangeDimension(bValue, !!bDefinition?.isRange)
      ) {
        return isEmptyRangeDimension(aValue, !!aDefinition?.isRange) ? -1 : 1
      }

      if (!aValue || !bValue) {
        return aValue ? -1 : 1
      }

      if (!Number.isNaN(Number(aValue)) && !Number.isNaN(Number(bValue))) {
        return Number(aValue) - Number(bValue)
      }

      return aValue && bValue
        ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
        : -1
    }

    return 0
  }
}

/**
 * A parameter with amendments has no renderable rows at all: row data is built from the
 * CSV-like `dimensionsAndValues` that only create operations carry, and rendering the
 * creates alone would show values that silently ignore the amends applied on top of them.
 */
export const generateTableRowEntries = (
  operations: OperationWithSourceInfo<ParameterValueEntityOperation>[],
  dimensionDefinitions: ParameterDimensionDefinitionInOut[],
): ParameterValueRowData[] => {
  const { creates, amends } = partitionValueOperations(operations)

  if (amends.length) {
    return []
  }

  return creates
    .flatMap((operation) =>
      flattenParameterValueCreateOperations(operation, dimensionDefinitions),
    )
    .sort(sortParameterValueRowData)
}

export const sortValueOperationsByToDateDescending = (
  operations: OperationWithSourceInfo<ParameterValueEntityOperation>[],
): OperationWithSourceInfo<ParameterValueEntityOperation>[] => {
  const toDateTime = (
    operation: OperationWithSourceInfo<ParameterValueEntityOperation>,
  ) => {
    const toDate = operation.internalOperation.entity?.toDate

    return toDate
      ? dateUtils.dateUtcAsLocal(toDate).valueOf()
      : Number.POSITIVE_INFINITY
  }

  return operations.toSorted((a, b) => toDateTime(b) - toDateTime(a))
}
